import { jsPDF } from "jspdf";
import "jspdf-autotable";
import axios from "axios";
import Swal from "sweetalert2";

// (2026-10) PDF de la Orden de Compra de producción externa. Vivía dentro de
// ListadoOrdenesCorteExterno; se sacó aquí sin cambios para abrirlo también
// desde el Reporte de Órdenes de Compra Producto Terminado.
const URL_ORDENES_CORTE_EXTERNO = process.env.REACT_APP_URL_ORDENES_CORTE_EXTERNO;
const URL_ARTICULOS_ORDENES_CORTE_EXTERNO =
  process.env.REACT_APP_URL_ARTICULOS_ORDENES_CORTE_EXTERNO;
const URL_TELAS = process.env.REACT_APP_URL_TELAS;

// L106 (2026-06) — logoInstitucionDonnatela cuando la empresa del pedido es UNIFORMES INNOVACIÓN
function logoInstSrc(empresa) {
  const t =
    typeof empresa === "string"
      ? empresa
      : empresa?.razonSocial || empresa?.name || "";
  const norm = t
    .normalize("NFD")
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .toLowerCase();
  const file = norm.includes("uniformes innovacion")
    ? "logoInstitucionDonnatela.png"
    : "logoInstitucion.png";
  return process.env.PUBLIC_URL + "/" + file;
}
// ============================================================================
// FICHA TÉCNICA DE BORDADOS (estilo Word): banda + rejilla de celdas con borde
// (imagen arriba, observación debajo). Autocontenida: hace su propio addPage
// y su propia paginación. Reutilizable en los PDFs que imprimen bordados.
// ============================================================================
async function dibujarFichaBordados(doc, bordadosFinal, opts) {
  const {
    getCloudinaryUrl,
    loadImagePDF,
    pageWidth,
    marginLeft,
    marginTop = 15,
    brandColor,
    lightBg = [245, 247, 250],
    borderColor = [210, 216, 224],
    labelColor = [110, 120, 135],
    s = (v) => (v == null ? "" : String(v)),
  } = opts || {};

  if (!Array.isArray(bordadosFinal) || !bordadosFinal.length) return;

  const pageHeight = doc.internal.pageSize.getHeight();
  const marginBottom = 15;
  const usableW = pageWidth - marginLeft * 2;

  // Carga robusta: fetch → blob → dataURL (evita "tainted canvas" por CORS);
  // fallback al loader que pase el archivo.
  const cargarImagen = async (url) => {
    if (!url) return null;
    try {
      const resp = await fetch(url, { mode: "cors" });
      if (resp.ok) {
        const blob = await resp.blob();
        const dataUrl = await new Promise((res) => {
          const r = new FileReader();
          r.onloadend = () => res(r.result);
          r.onerror = () => res(null);
          r.readAsDataURL(blob);
        });
        if (dataUrl) return dataUrl;
      }
    } catch (e) {
      /* fallback abajo */
    }
    if (typeof loadImagePDF === "function") {
      return await loadImagePDF(url).catch(() => null);
    }
    return null;
  };

  doc.addPage();
  let y = marginTop;

  const room = (needed) => {
    if (y + needed > pageHeight - marginBottom) {
      doc.addPage();
      y = marginTop;
    }
  };

  // Banda "FICHA TÉCNICA DE BORDADOS"
  doc.setFillColor(...brandColor);
  doc.rect(marginLeft, y, usableW, 9, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, "bold");
  doc.setFontSize(12.5);
  doc.text("FICHA TÉCNICA DE BORDADOS", pageWidth / 2, y + 6.3, {
    align: "center",
  });
  doc.setTextColor(0);
  doc.setFont(undefined, "normal");
  y += 14;

  const cols = 3;
  const cellGap = 6;
  const cellW = (usableW - cellGap * (cols - 1)) / cols;
  const cellPad = 3;
  const imgAreaH = 46;
  const capFont = 8.5;
  const capLineH = 4;

  for (const item of bordadosFinal) {
    // Encabezado del artículo (banda clara con borde)
    const headH = 8;
    room(headH + imgAreaH + 16);
    doc.setFillColor(...lightBg);
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.3);
    doc.rect(marginLeft, y, usableW, headH, "FD");
    doc.setDrawColor(0);
    doc.setFont(undefined, "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...brandColor);
    doc.text(
      `${s(item.articulo)}   ·   Área: ${s(item.area)}`,
      marginLeft + 3,
      y + headH - 2.5
    );
    doc.setTextColor(0);
    doc.setFont(undefined, "normal");
    y += headH + 3;

    const itemsB = Array.isArray(item.bordados) ? item.bordados : [];
    for (let i = 0; i < itemsB.length; i += cols) {
      const rowItems = itemsB.slice(i, i + cols);

      // preparar: cargar imagen + medir caption para nivelar el alto de la fila
      const prepared = [];
      let maxCapH = 0;
      for (const bordado of rowItems) {
        const imgData = await cargarImagen(getCloudinaryUrl(bordado.public_id));
        const capLines = bordado.observacion
          ? doc.splitTextToSize(String(bordado.observacion), cellW - cellPad * 2)
          : [];
        const capH = capLines.length * capLineH;
        if (capH > maxCapH) maxCapH = capH;
        prepared.push({ imgData, capLines });
      }

      const rowH = imgAreaH + (maxCapH > 0 ? maxCapH + 4 : 2) + cellPad;
      room(rowH + 4);

      let cx = marginLeft;
      for (const cell of prepared) {
        // borde de la celda
        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.3);
        doc.rect(cx, y, cellW, rowH);
        doc.setDrawColor(0);

        // imagen centrada en el área superior
        if (cell.imgData) {
          const props = doc.getImageProperties(cell.imgData);
          let w = cellW - cellPad * 2;
          let h = (props.height * w) / props.width;
          const maxH = imgAreaH - cellPad * 2;
          if (h > maxH) {
            h = maxH;
            w = (props.width * h) / props.height;
          }
          const ox = cx + (cellW - w) / 2;
          const oy = y + cellPad + (imgAreaH - cellPad * 2 - h) / 2;
          doc.addImage(cell.imgData, "PNG", ox, oy, w, h);
        } else {
          doc.setFontSize(7);
          doc.setTextColor(150);
          doc.text("Sin imagen", cx + cellW / 2, y + imgAreaH / 2, {
            align: "center",
          });
          doc.setTextColor(0);
        }

        // caption (observación) debajo de una línea divisoria
        if (cell.capLines.length) {
          doc.setDrawColor(...borderColor);
          doc.line(cx, y + imgAreaH, cx + cellW, y + imgAreaH);
          doc.setDrawColor(0);
          doc.setFont(undefined, "normal");
          doc.setFontSize(capFont);
          doc.setTextColor(...labelColor);
          doc.text(cell.capLines, cx + cellPad, y + imgAreaH + capLineH + 1);
          doc.setTextColor(0);
        }
        cx += cellW + cellGap;
      }
      y += rowH + cellGap;
    }
    y += 3;
  }
  doc.setLineWidth(0.2);
  return y;
}

export async function PDFOC(
  numero,
  cliente,
  proveedor,
  proveedorDireccion,
  proveedorMail,
  fechaCompromiso,
  totalGeneral,
  fecha,
  pedido,
  idPedidos,
  totalPiezas,
  observaciones,
  observacionesMarca,
  marca,
  idPDFOC,
  idProveedor,
  // L40 (2026-06)
  nombreFirma = "",
  // L88 (2026-06) — si true agrupa por (articulo+precio+tela+area)
  separarPorArea = false,
  // L106 (2026-06) — empresa del pedido (para elegir el logo)
  empresa = "",
  // L128 (2026-07) — objeto empresa completo (datos fiscales del header)
  empresaObj = null,
  // (2026-08) true = SOLO la hoja de habilitación (botón aparte en el listado)
  soloHabilitacion = false
) {
  const getImageAsDataURL = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          resolve(canvas.toDataURL("image/png"));
        } catch (e) {
          reject(e);
        }
      };

      img.onerror = reject;
      img.src = url;
    });
  };

  const chunk = (arr, size) => {
    const out = [];

    for (let i = 0; i < arr.length; i += size) {
      out.push(arr.slice(i, i + size));
    }

    return out;
  };

  const s = (v) => {
    if (v === null || v === undefined) return "";

    if (
      typeof v === "string" &&
      v.trim().toUpperCase() === "NA"
    )
      return "";

    return typeof v === "string" ? v : String(v);
  };

  const sn = (v, def = 0) => {
    if (v === null || v === undefined) return def;

    if (
      typeof v === "string" &&
      v.trim().toUpperCase() === "NA"
    )
      return def;

    const n = Number(v);

    return Number.isFinite(n) ? n : def;
  };

  const isSinVar = (name) => {
    const t = s(name).trim().toLowerCase();

    return !t || t === "sin variante" || t === "na";
  };

  const money = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n) || 0);

  const collatorES = new Intl.Collator("es", {
    sensitivity: "base",
    numeric: true,
  });

  try {
    const { data: allArticulosOrdenCorte } =
      await axios.get(
        `${URL_ARTICULOS_ORDENES_CORTE_EXTERNO}/ordenPDF/${idPDFOC}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem(
              "app_token"
            )}`,
          },
        }
      );

    // L52 (2026-06) — fetch articulosPedido para tener observacionesArticulo
    let allArticulosPedido = [];
    if (idPedidos) {
      try {
        const URL_ARTICULOS_PEDIDO_LOCAL = process.env.REACT_APP_URL_ARTICULOS_PEDIDO;
        const { data: apData } = await axios.get(
          `${URL_ARTICULOS_PEDIDO_LOCAL}/pedidos/${idPedidos}`,
          {
            headers: {
              Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
            },
          }
        );
        allArticulosPedido = Array.isArray(apData) ? apData : [];
      } catch (e) {
        console.warn("No se pudo cargar articulosPedido:", e);
      }
    }

    // Match por pedido + articulo + area + color (fallback sin color)
    const findApMatch = (a) => {
      const pedidoId = s(a?.pedidos?.[0]?._id || a?.pedidos?.[0]);
      const articuloId = s(a?.articulos?.[0]?._id || a?.articulos?.[0]);
      const areaId = s(a?.areasClientes?.[0]?._id || a?.areasClientes?.[0]);
      const colorId = s(a?.colores?.[0]?._id || a?.colores?.[0]);
      const exact = allArticulosPedido.find((x) => {
        const apPedidoId = s(x?.pedidos?.[0]?._id || x?.pedidos?.[0]);
        const apArticuloId = s(x?.articulos?.[0]?._id || x?.articulos?.[0]);
        const apAreaId = s(x?.areasClientes?.[0]?._id || x?.areasClientes?.[0]);
        const apColorId = s(x?.colores?.[0]?._id || x?.colores?.[0]);
        return (
          apPedidoId === pedidoId &&
          apArticuloId === articuloId &&
          apAreaId === areaId &&
          (colorId && apColorId ? apColorId === colorId : true)
        );
      });
      if (exact) return exact;
      return allArticulosPedido.find((x) => {
        const apPedidoId = s(x?.pedidos?.[0]?._id || x?.pedidos?.[0]);
        const apArticuloId = s(x?.articulos?.[0]?._id || x?.articulos?.[0]);
        const apAreaId = s(x?.areasClientes?.[0]?._id || x?.areasClientes?.[0]);
        return (
          apPedidoId === pedidoId &&
          apArticuloId === articuloId &&
          apAreaId === areaId
        );
      });
    };

    const getObservacionesArticulo = (a) => {
      const ap = findApMatch(a);
      return s(
        ap?.observacionesArticulo ||
          a?.articulosPedido?.[0]?.observacionesArticulo ||
          a?.observacionesArticulo ||
          ""
      );
    };

    const doc = new jsPDF({
      orientation: "landscape",
    });

    const pageWidth =
      doc.internal.pageSize.getWidth();

    const pageHeight =
      doc.internal.pageSize.getHeight();

    const marginLeft = 15;
    const marginRight = 15;
    const marginBottom = 15;

    // === L36 (2026-06) — Paleta y helpers de diseño ===
    // L114 (2026-06) — morado si la empresa es UNIFORMES INNOVACIÓN (logo Donnatela)
    const brandColor = /Donnatela/i.test(logoInstSrc(empresa))
      ? [205, 184, 245]
      : [42, 90, 138];
    const lightBg = [245, 247, 250];
    const borderColor = [210, 216, 224];
    const labelColor = [110, 120, 135];
    const stripeBg = [251, 252, 253];

    const drawLabel = (label, value, lblX, valX, y) => {
      doc.setFont(undefined, "bold");
      doc.setTextColor(...labelColor);
      doc.setFontSize(8.5);
      doc.text(label, lblX, y);
      doc.setFont(undefined, "normal");
      doc.setTextColor(0);
      doc.setFontSize(9.5);
      const txt = s(value).trim();
      doc.text(txt || "—", valX, y);
    };

    // (2026-08) Encabezado reutilizable: también lo usa la hoja de habilitación
    const dibujarEncabezado = async (titulo) => {
      // ============ LOGO IZQUIERDA ============
      const logo = new Image();
      logo.src = logoInstSrc(empresa); // L106
      await new Promise((resolve) => {
        logo.onload = () => {
          try {
            const maxH = /Donnatela/i.test(logo.src) ? 22 : 18; // L115 logo Donnatela más grande
            const scale = maxH / logo.height;
            const w = logo.width * scale;
            const h = logo.height * scale;
            doc.addImage(logo, "PNG", marginLeft, 8, w, h);
          } catch (e) {
            console.warn("No se pudo agregar logo", e);
          }
          resolve();
        };
        logo.onerror = () => resolve();
      });

      // ============ TÍTULO + LÍNEA DIVISORIA ============
      doc.setFont(undefined, "bold");
      doc.setFontSize(16);
      doc.setTextColor(...brandColor);
      doc.text(titulo, pageWidth - marginLeft, 18, { align: "right" });
      doc.setTextColor(0);

      // L128 (2026-07) — Datos fiscales de la empresa propia, debajo del título
      // (réplica de ListadoPedidos.jsx "L32"). Se dibuja a la derecha, bajo el logo.
      let headerLineY = 28;
      if (empresaObj) {
        let empY = 23;
        doc.setFont(undefined, "bold");
        doc.setFontSize(9.5);
        doc.setTextColor(60, 60, 60);
        doc.text(
          s(empresaObj.razonSocial) || s(empresaObj.name) || "",
          pageWidth - marginLeft,
          empY,
          { align: "right" }
        );
        empY += 4;
        doc.setFont(undefined, "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...labelColor);
        const fiscalLine = [
          empresaObj.rfc ? `RFC: ${s(empresaObj.rfc)}` : "",
          empresaObj.regimenFiscal ? `Rég. Fiscal: ${s(empresaObj.regimenFiscal)}` : "",
        ]
          .filter((x) => x)
          .join("   ·   ");
        if (fiscalLine) {
          doc.text(fiscalLine, pageWidth - marginLeft, empY, { align: "right" });
          empY += 4;
        }
        const direccionStr = [
          s(empresaObj.direccionFiscal),
          empresaObj.cp ? `C.P. ${s(empresaObj.cp)}` : "",
        ]
          .filter((x) => x)
          .join(", ");
        if (direccionStr) {
          const dirAvailW = pageWidth - marginLeft - (marginLeft + 60); // deja espacio al logo
          const dirLines = doc.splitTextToSize(direccionStr, dirAvailW);
          dirLines.forEach((line) => {
            doc.text(line, pageWidth - marginLeft, empY, { align: "right" });
            empY += 3.8;
          });
        }
        doc.setTextColor(0);
        // corre la línea divisoria abajo del bloque fiscal para no encimar
        headerLineY = Math.max(28, empY + 1);
      }

      doc.setDrawColor(...brandColor);
      doc.setLineWidth(0.6);
      doc.line(marginLeft, headerLineY, pageWidth - marginLeft, headerLineY);
      doc.setLineWidth(0.2);
      doc.setDrawColor(0);

      // ============ CARD 2 COLUMNAS DATOS ============
      const cardY = headerLineY + 3;
      const cardW = pageWidth - marginLeft * 2;
      // L128 (2026-07) — un renglón más alto para alojar el Vendedor debajo de Monarch/Total
      const cardH = 37;
      doc.setDrawColor(...borderColor);
      doc.setFillColor(...lightBg);
      doc.roundedRect(marginLeft, cardY, cardW, cardH, 1.5, 1.5, "FD");
      doc.setDrawColor(0);

      // Columna izquierda
      const lblX_L = marginLeft + 3;
      const valX_L = marginLeft + 28;
      const baseY = cardY + 6;
      drawLabel("Fecha:", fecha, lblX_L, valX_L, baseY);
      drawLabel("Cliente:", cliente, lblX_L, valX_L, baseY + 5);
      drawLabel("Marca:", marca, lblX_L, valX_L, baseY + 10);
      drawLabel("Monarch:", observacionesMarca, lblX_L, valX_L, baseY + 15);
      drawLabel("Vendedor:", nombreFirma, lblX_L, valX_L, baseY + 20);
      // drawLabel("Total:", money(totalGeneral), lblX_L, valX_L, baseY + 25);

      // Columna derecha
      const colRightX = pageWidth / 2 + 5;
      const lblX_R = colRightX;
      const valX_R = colRightX + 30;
      drawLabel("Fecha Comp.:", fechaCompromiso, lblX_R, valX_R, baseY);
      drawLabel(
        "Proveedor:",
        `${idProveedor ? "#" + idProveedor + " " : ""}${s(proveedor) || ""}`,
        lblX_R,
        valX_R,
        baseY + 5
      );
      drawLabel("Dirección:", proveedorDireccion, lblX_R, valX_R, baseY + 10);
      drawLabel("Mail:", proveedorMail, lblX_R, valX_R, baseY + 15);
      // L128 (2026-07) — Vendedor del pedido en el bloque derecho, DEBAJO del nivel
      // de "Monarch" (baseY+15) y "Total" (baseY+20) de la columna izquierda.

      return cardY + cardH + 6;
    };

    let currentY = await dibujarEncabezado(
      soloHabilitacion
        ? `HABILITACIÓN — OC # ${s(numero)}`
        : `ORDEN COMPRA # ${s(numero)}`
    );

    // (2026-08) Firmas reutilizables (hoja de OC y hoja de habilitación)
    const dibujarFirmas = () => {
      // ================= FIRMAS =================
      try {
        // L81 (2026-06) — Posicionar firmas al pie de la última página, midiendo
        // contra currentY real (que sí contempla la sección de BORDADOS dibujada
        // manualmente). Si no cabe, agrega página nueva.
        // currentY ya refleja el fin real del contenido (totales / bordados /
        // habilitaciones). No usar finalAutoY porque puede apuntar a una tabla de
        // una página anterior y empujar las firmas a otra hoja sin necesidad.
        const yReferencia = currentY || 0;
        // firmas justo debajo del contenido (no pegadas al fondo). Solo salta de
        // hoja si el bloque de firmas (~35mm) no cabe en lo que resta de la página.
        let firmaY = yReferencia + 20;
        if (firmaY + 35 > pageHeight - marginBottom) {
          doc.addPage();
          firmaY = 40;
        }

        const firmaWidth = 80;
        const firmaXIzq = 15;
        const firmaXDer = pageWidth - 15 - firmaWidth;

        doc.setLineWidth(0.3);
        doc.line(firmaXIzq, firmaY, firmaXIzq + firmaWidth, firmaY);
        doc.line(firmaXDer, firmaY, firmaXDer + firmaWidth, firmaY);

        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        doc.text(
          "Firma Habilitacion",
          firmaXIzq + firmaWidth / 2,
          firmaY + 5,
          { align: "center" }
        );
        doc.text(
          "Firma Proveedor",
          firmaXDer + firmaWidth / 2,
          firmaY + 5,
          { align: "center" }
        );

        doc.setFont(undefined, "normal");
        doc.setFontSize(8);
        if (proveedor) {
          const proveedorTxt = `${idProveedor ? `#${idProveedor} ` : ""}${proveedor}`;
          doc.text(
            proveedorTxt,
            firmaXDer + firmaWidth / 2,
            firmaY + 10,
            { align: "center" }
          );
        }
        if (fecha) {
          doc.text(
            `Fecha: ${fecha}`,
            firmaXIzq + firmaWidth / 2,
            firmaY + 10,
            { align: "center" }
          );
          doc.text(
            `Fecha: ${fecha}`,
            firmaXDer + firmaWidth / 2,
            firmaY + 15,
            { align: "center" }
          );
        }
      } catch (errFirmas) {
        console.warn("No se pudieron dibujar firmas:", errFirmas);
      }
    };

    // (2026-08) Botón aparte: SOLO la hoja de habilitación (mismo encabezado y firmas)
    if (soloHabilitacion) {
    // ================= HABILITACIONES =================
    try {
      const URL_DETALLE_HABILITACION_ORDENES_CORTES_LOCAL =
        process.env.REACT_APP_URL_DETALLE_HABILITACION_ORDENES_CORTES;
      const { data: habilitaciones } = await axios.get(
        `${URL_DETALLE_HABILITACION_ORDENES_CORTES_LOCAL}/pedido/${idPedidos}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      );

      if (Array.isArray(habilitaciones) && habilitaciones.length) {
        const yHab = currentY;

        const tablaHabilitaciones = habilitaciones.map((a) => [
          a.habilitacionOrdenesCortes?.[0]?.fecha || "-",
          `${a.habilitacion?.[0]?.nombre || ""} ${
            a.habilitacion?.[0]?.descripcion || ""
          }`.trim(),
          sn(a.cantidad, 0),
        ]);

        doc.autoTable({
          head: [["Fecha", "Descripcion", "Cantidad"]],
          body: tablaHabilitaciones,
          startY: yHab,
          margin: { left: 15, right: 15 },
          styles: {
            fontSize: 9,
            cellPadding: 1,
            valign: "middle",
            halign: "center",
          },
          headStyles: {
            fillColor: [41, 128, 186],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
          columnStyles: {
            0: { cellWidth: 40 },
            2: { cellWidth: 30 },
          },
        });
        // reflejar el fin de la tabla de habilitaciones en currentY
        currentY =
          (doc.autoTable.previous && doc.autoTable.previous.finalY) ||
          currentY;
      }
    } catch (errHab) {
      console.warn("No se pudieron cargar habilitaciones:", errHab);
    }


      dibujarFirmas();

      try {
        const pageCount = doc.internal.getNumberOfPages();
        for (let p = 1; p <= pageCount; p++) {
          doc.setPage(p);
          doc.setFontSize(8);
          doc.setTextColor(...labelColor);
          doc.text(
            `Habilitación OC #${s(numero)}  ·  ${s(cliente)}  ·  Página ${p} de ${pageCount}`,
            pageWidth / 2,
            pageHeight - 6,
            { align: "center" }
          );
          doc.setTextColor(0);
        }
      } catch (e) {}

      window.open(doc.output("bloburl"), "_blank");
      return;
    }


    const ensureRoom = (needed = 20) => {
      if (
        currentY + needed >
        pageHeight - marginBottom
      ) {
        doc.addPage();
        currentY = 20;
      }
    };

    // ============ OBSERVACIONES — L36 caja con label band ============
    const obsTextoOC = String(observaciones || "").trim().replace(/\t/g, "  ").replace(/\r/g, "");
    if (obsTextoOC && obsTextoOC.toUpperCase() !== "NA") {
      ensureRoom(16);
      doc.setFillColor(...brandColor);
      doc.rect(marginLeft, currentY, pageWidth - marginLeft * 2, 6, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, "bold");
      doc.setFontSize(10);
      doc.text("OBSERVACIONES", marginLeft + 3, currentY + 4.2);
      doc.setTextColor(0);
      currentY += 6;

      doc.setFont(undefined, "normal");
      doc.setFontSize(9);
      const obsLines = doc.splitTextToSize(obsTextoOC, pageWidth - marginLeft * 2 - 12);
      const lineHeightObs = 4.2;
      const blockHeightObs = obsLines.length * lineHeightObs;

      ensureRoom(blockHeightObs + 6);
      doc.setFillColor(...lightBg);
      doc.setDrawColor(...borderColor);
      doc.rect(marginLeft, currentY, pageWidth - marginLeft * 2, blockHeightObs + 5, "FD");
      doc.setDrawColor(0);
      doc.text(obsLines, marginLeft + 3, currentY + 4);
      currentY += blockHeightObs + 9;
    }

    // (2026-07) Catálogo de Telas para el fallback de composición (igual que el
    // PDFOC de ListadoPedidos): si el articuloPedido/orden no trae composición, se
    // busca por NOMBRE de tela en el catálogo.
    let catalogoTelasPDF = [];
    try {
      const { data: telasCat } = await axios.get(URL_TELAS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      });
      catalogoTelasPDF = Array.isArray(telasCat) ? telasCat : [];
    } catch (eTelas) {
      console.warn(
        "No se pudo cargar el catálogo de telas para la composición:",
        eTelas
      );
    }

    const grupoPorArticulo = {};

    allArticulosOrdenCorte.forEach((a) => {
      const linea =
        a.articulos?.[0]?.subDepartamentos?.[0]?.name ||
        a.articulos?.[0]?.subDepartamentos?.[0]?.nombre ||
        "";
      const articuloTexto =
        `${a.articulos?.[0]?.codigo || ""}${
          linea ? " " + linea : ""
        }`.trim() || "Sin Artículo";

      const precio = sn(a.precio, 0);

      // L134 (2026-07) — tela/composición POR COLOR: se toman del articulosPedido que hace match por color
      // (a.tela puede venir a nivel modelo/igual para todos los colores → no separaba). ArticulosPedido tiene tela y composicion por color.
      const apTela = findApMatch(a);
      const telaNombre = s(apTela?.tela || a.tela) || "Sin Tela";
      let telaComp = s(apTela?.composicion || a.composicion);
      if (telaComp === "NA") telaComp = "";
      // (2026-07) fallback: si no hay composición, jalarla del catálogo de Telas
      // por nombre (igual que el PDFOC de ListadoPedidos).
      if (!telaComp && telaNombre && telaNombre !== "Sin Tela") {
        const tObj = catalogoTelasPDF.find(
          (x) =>
            (x?.nombre || "").trim().toLowerCase() ===
            telaNombre.trim().toLowerCase()
        );
        telaComp = s(tObj?.composicion);
        if (telaComp === "NA") telaComp = "";
      }

      // L88 (2026-06) — si separarPorArea, incluir área en la clave
      const areaNombre = separarPorArea
        ? s(a?.areasClientes?.[0]?.name) || "Sin Área"
        : "";
      // L133 (2026-07) — separar también por composición: si es otra tela por color
      // (mismo nombre pero distinta composición, o nombre distinto), cae en otro bloque.
      // Se agrega al final de la clave para NO recorrer los índices ya usados (tela=[2], área=[3]).
      const clave = separarPorArea
        ? `${articuloTexto}||${precio}||${telaNombre}||${areaNombre}||${telaComp}`
        : `${articuloTexto}||${precio}||${telaNombre}||${telaComp}`;

      if (!grupoPorArticulo[clave]) {
        grupoPorArticulo[clave] = { items: [], composiciones: new Set(), area: areaNombre };
      }

      grupoPorArticulo[clave].items.push(a);
      if (telaComp) grupoPorArticulo[clave].composiciones.add(telaComp);
    });

    const ordenTallas = ["2XCH","XCH","EXTRA CHICA","CHICA","CH","M","MEDIANA","GRANDE","G","EXTRA GRANDE","XG","2XG","XXG","3XG","XXXG","4XG","XXXXG","5XG","XXXXXG","ESP","VARIOS","2","4","6","8","9","10","11","12","13","14","15","16","17","18","19","20","21","22","23","24","25","26","27","28","29","30","31","32","33","34","35","36","37","38","39","40","41","42","43","44","45","46","47","48","49","50","51","52","53","54","55","56"];

    const getOrdenTalla = (talla) => {
      const base = String(talla || "")
        .trim()
        .toUpperCase()
        .split(" ")[0];

      const idx =
        ordenTallas.indexOf(base);

      return idx === -1 ? 999 : idx;
    };

    // L128 (2026-07) — acumulador de subtotal (precio × piezas por cada modelo)
    let subtotalPDF = 0;

    for (const clave in grupoPorArticulo) {
      const bucket = grupoPorArticulo[clave];
      const grupo = bucket.items;
      const composicionGrupo = Array.from(bucket.composiciones).join(" / ");

      // Omitir el artículo por completo si todas sus tallas/colores están en 0
      const totalGrupo = grupo.reduce((acc, a) => acc + sn(a.cantidad, 0), 0);
      if (totalGrupo <= 0) continue;

      const partesClave = clave.split("||");
      const articulo = partesClave[0];
      const precio = partesClave[1];
      const tela = partesClave[2];
      // L88 (2026-06)
      const areaClave = separarPorArea ? partesClave[3] || bucket.area || "" : "";

      const telaDisplay =
        composicionGrupo && tela && tela !== "Sin Tela"
          ? `${tela} - ${composicionGrupo}`
          : composicionGrupo || tela || "Sin Tela";

      // === L36 (2026-06) — Banda azul como header del grupo ===
      ensureRoom(22);
      const bandH = 8;
      doc.setFillColor(...brandColor);
      doc.rect(marginLeft, currentY, pageWidth - marginLeft * 2, bandH, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, "bold");
      doc.setFontSize(10.5);
      // L109 (2026-06) — DEPARTAMENTO del artículo (DAMA/CABALLERO) en el band
      const deptoGrupo = s(
        grupo[0]?.articulos?.[0]?.departamentos?.[0]?.name ||
          grupo[0]?.articulos?.[0]?.departamentos?.[0]?.nombre ||
          ""
      );
      const deptoTxt = deptoGrupo ? `   ·   DEPTO: ${deptoGrupo}` : "";
      // L128 (2026-07) — anteponer "Modelo" al nombre del artículo en cada renglón/grupo
      const modeloTxt = `Modelo: ${s(articulo) || "Artículo sin nombre"}`;
      const bandTitle = separarPorArea && areaClave
        ? `${modeloTxt}${deptoTxt}   ·   Tela: ${s(telaDisplay) || "Sin Tela"}   ·   Área: ${areaClave}`
        : `${modeloTxt}${deptoTxt}   ·   Tela: ${s(telaDisplay) || "Sin Tela"}`;
      // L131 (2026-07) — el precio va a la derecha; el título se encoge si es muy largo para no encimarlo
      const precioStr = `PRECIO ${money(precio)}`;
      const precioW = doc.getTextWidth(precioStr); // medido a 10.5 bold
      const titleX = marginLeft + 3;
      const titleMaxW =
        pageWidth - marginLeft - 3 - precioW - 6 - titleX; // hueco antes del precio
      const titleW105 = doc.getTextWidth(bandTitle);
      const titleSize =
        titleW105 > titleMaxW
          ? Math.max(6.5, 10.5 * (titleMaxW / titleW105))
          : 10.5;
      doc.setFontSize(titleSize);
      doc.text(bandTitle, titleX, currentY + bandH - 2.5);
      doc.setFontSize(10.5);
      doc.text(precioStr, pageWidth - marginLeft - 3, currentY + bandH - 2.5, { align: "right" });
      doc.setTextColor(0);
      doc.setFont(undefined, "normal");
      currentY += bandH + 3;

      // === L52 (2026-06) — Observaciones del artículo (italic gris) ===
      const obsArticuloGrupo = [
        ...new Set(
          grupo
            .map((a) => getObservacionesArticulo(a).trim())
            .filter((x) => x)
        ),
      ].join(" / ");

      if (obsArticuloGrupo) {
        doc.setFont(undefined, "italic");
        doc.setTextColor(...labelColor);
        doc.setFontSize(9);
        const maxWidthObsArticulo = pageWidth - marginLeft * 2;
        const obsArticuloLines = doc.splitTextToSize(
          `Observaciones del artículo: ${obsArticuloGrupo}`,
          maxWidthObsArticulo
        );
        const obsArticuloHeight = obsArticuloLines.length * 4.2;
        ensureRoom(obsArticuloHeight + 2);
        doc.text(obsArticuloLines, marginLeft + 2, currentY);
        currentY += obsArticuloHeight + 3;
        doc.setFont(undefined, "normal");
        doc.setTextColor(0);
      }

      const data = {};
      const totalVariante = {};
      const totalColor = {};
      const totalTallaModelo = {};
      const tallasMap = new Map();

      grupo.forEach((a) => {
        const color =
          s(a.colores?.[0]?.name) ||
          "Sin Color";

        const tallaBase =
          s(a.tallas?.[0]?.name).trim() ||
          "Sin Talla";

        const varianteNameRaw = s(
          a.tallas?.[0]?.variantesTallas?.[0]
            ?.name
        ).trim();

        const varianteName =
          varianteNameRaw || "Sin variante";

        const cantidad = sn(a.cantidad, 0);

        if (!data[color]) data[color] = {};

        if (!data[color][varianteName]) {
          data[color][varianteName] = {};
        }

        if (
          !data[color][varianteName][
            tallaBase
          ]
        ) {
          data[color][varianteName][
            tallaBase
          ] = 0;
        }

        data[color][varianteName][
          tallaBase
        ] += cantidad;

        if (!totalVariante[color]) {
          totalVariante[color] = {};
        }

        if (
          !totalVariante[color][
            varianteName
          ]
        ) {
          totalVariante[color][
            varianteName
          ] = 0;
        }

        totalVariante[color][
          varianteName
        ] += cantidad;

        if (!totalColor[color]) {
          totalColor[color] = 0;
        }

        totalColor[color] += cantidad;

        if (!totalTallaModelo[tallaBase]) {
          totalTallaModelo[tallaBase] = 0;
        }

        totalTallaModelo[tallaBase] +=
          cantidad;

        tallasMap.set(tallaBase, true);
      });

      const totalPiezasModelo =
        Object.values(totalColor).reduce(
          (acc, val) =>
            acc + sn(val, 0),
          0
        );

      // L128 (2026-07) — sumar importe del modelo (precio unitario × piezas)
      subtotalPDF += sn(precio, 0) * totalPiezasModelo;

      const tallasOrdenadas =
        Array.from(
          tallasMap.keys()
        ).sort((a, b) => {
          const diff =
            getOrdenTalla(a) -
            getOrdenTalla(b);

          if (diff !== 0) return diff;

          return collatorES.compare(
            String(a),
            String(b)
          );
        });

      const usableWidth =
        pageWidth - marginLeft * 2;

      const colorW = 34;
      const totalW = 14;
      const minTallaW = 8.5;

      const maxTallasPerRow =
        Math.max(
          1,
          Math.floor(
            (usableWidth -
              colorW -
              totalW) /
              minTallaW
          )
        );

      const chunkSize = Math.max(
        1,
        Math.min(
          maxTallasPerRow,
          tallasOrdenadas.length || 1
        )
      );

      const tallasChunks = chunk(
        tallasOrdenadas,
        chunkSize
      );

      const colores = Object.keys(
        data
      ).sort((a, b) =>
        collatorES.compare(a, b)
      );

      for (
        let ci = 0;
        ci < tallasChunks.length;
        ci++
      ) {
        const chunkTallas =
          tallasChunks[ci];

        const nTallas =
          chunkTallas.length;

        const tallaW =
          (usableWidth -
            colorW -
            totalW) /
          Math.max(1, nTallas);

        ensureRoom(24);

        const headChunk = [
          ["Color", ...chunkTallas, "Total"],
        ];

        const bodyChunk = [];

        for (const color of colores) {
          const variantesRaw =
            Object.keys(
              data[color] || {}
            );

          const variantes =
            variantesRaw.sort(
              (a, b) => {
                const aSin =
                  isSinVar(a);

                const bSin =
                  isSinVar(b);

                if (
                  aSin &&
                  !bSin
                )
                  return -1;

                if (
                  !aSin &&
                  bSin
                )
                  return 1;

                return collatorES.compare(
                  a,
                  b
                );
              }
            );

          let firstVar = true;

          for (const variante of variantes) {
            const totVar = sn(
              totalVariante?.[color]?.[
                variante
              ],
              0
            );

            if (totVar <= 0) continue;

            const row =
              new Array(
                1 + nTallas + 1
              ).fill("");

            const tituloVar =
              isSinVar(variante)
                ? ""
                : s(variante);

            const colorTxt =
              firstVar
                ? s(color)
                : "";

            row[0] = tituloVar
              ? colorTxt
                ? `${colorTxt}\n${tituloVar}`
                : `\n${tituloVar}`
              : colorTxt || "";

            chunkTallas.forEach(
              (talla, idx) => {
                const val = sn(
                  data?.[color]?.[
                    variante
                  ]?.[talla],
                  0
                );

                row[1 + idx] =
                  val > 0
                    ? String(val)
                    : "";
              }
            );

            row[row.length - 1] =
              String(totVar);

            bodyChunk.push(row);

            firstVar = false;
          }

          const variantesDelColor =
            Object.keys(
              data[color] || {}
            ).filter(
              (v) => !isSinVar(v)
            );

          if (
            variantesDelColor.length > 0 &&
            sn(totalColor[color], 0) > 1
          ) {
            const rowTotalColor =
              new Array(
                1 + nTallas + 1
              ).fill("");

            rowTotalColor[0] =
              `Total ${color}`;

            chunkTallas.forEach(
              (talla, idx) => {
                let totalTallaColor = 0;

                Object.keys(
                  data[color] || {}
                ).forEach(
                  (variante) => {
                    totalTallaColor += sn(
                      data?.[color]?.[
                        variante
                      ]?.[talla],
                      0
                    );
                  }
                );

                rowTotalColor[
                  1 + idx
                ] =
                  totalTallaColor > 0
                    ? String(
                        totalTallaColor
                      )
                    : "";
              }
            );

            rowTotalColor[
              rowTotalColor.length - 1
            ] = String(
              sn(totalColor[color], 0)
            );

            bodyChunk.push(
              rowTotalColor
            );
          }
        }

        // L132 (2026-07) — el renglón "TOTAL PIEZAS" dentro de la tabla se quitó;
        // el total queda solo en el recuadro "TOTAL PIEZAS: n" debajo de la tabla.

        const columnStyles = {
          0: {
            cellWidth: colorW,
            halign: "left",
          },
          [1 + nTallas]: {
            cellWidth: totalW,
            halign: "center",
          },
        };

        for (
          let i = 0;
          i < nTallas;
          i++
        ) {
          columnStyles[1 + i] = {
            cellWidth: tallaW,
            halign: "center",
          };
        }

        doc.autoTable({
          head: headChunk,
          body: bodyChunk,
          startY: currentY,
          theme: "grid",
          margin: {
            left: marginLeft,
            right: marginRight,
          },
          tableWidth: usableWidth,
          // L39 (2026-06) — renglones lo más compactos posible (vertical pad mínimo)
          styles: {
            fontSize: 10,
            cellPadding: { top: 0.3, right: 1, bottom: 0.3, left: 1 },
            valign: "middle",
            halign: "center",
            overflow: "linebreak",
            lineColor: borderColor,
            lineWidth: 0.15,
            minCellHeight: 0,
          },
          headStyles: {
            fillColor: brandColor,
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 10,
            halign: "center",
            cellPadding: { top: 0.6, right: 1, bottom: 0.6, left: 1 },
            minCellHeight: 0,
          },
          alternateRowStyles: {
            fillColor: stripeBg,
          },
          columnStyles,
          didParseCell: (
            hookData
          ) => {
            if (
              hookData.section ===
              "body"
            ) {
              const raw =
                hookData.row.raw?.[0] ||
                "";

              if (
                hookData.column
                  .index === 0
              ) {
                hookData.cell.styles.halign =
                  "left";

                const txt =
                  typeof hookData
                    .cell.raw ===
                  "string"
                    ? hookData
                        .cell.raw
                    : hookData
                        .cell.raw
                        ?.content || "";

                if (
                  txt &&
                  String(
                    txt
                  ).includes("\n")
                ) {
                  hookData.cell.styles.fontStyle =
                    "bold";
                }
              }

              if (
                String(
                  raw
                ).startsWith(
                  "Total "
                ) ||
                String(raw) ===
                  "TOTAL PIEZAS"
              ) {
                hookData.cell.styles.fontStyle =
                  "bold";

                hookData.cell.styles.fillColor =
                  [235, 235, 235];

                hookData.cell.styles.cellPadding =
                  {
                    top: 0.3,
                    right: 1,
                    bottom: 0.3,
                    left: 1,
                  };
                hookData.cell.styles.minCellHeight = 0;
              }
            }
          },
        });

        currentY =
          doc.autoTable.previous.finalY +
          6;
      }

      // L36 (2026-06) — Total piezas del modelo como chip
      ensureRoom(10);
      const chipText = `TOTAL PIEZAS: ${totalPiezasModelo}`;
      doc.setFontSize(9.5);
      doc.setFont(undefined, "bold");
      const chipPad = 3;
      const chipW = doc.getTextWidth(chipText) + chipPad * 2;
      const chipH = 6;
      const chipX = pageWidth - marginLeft - chipW;
      doc.setFillColor(...lightBg);
      doc.setDrawColor(...brandColor);
      doc.setLineWidth(0.3);
      doc.roundedRect(chipX, currentY, chipW, chipH, 1.5, 1.5, "FD");
      doc.setLineWidth(0.2);
      doc.setTextColor(...brandColor);
      doc.text(chipText, chipX + chipPad, currentY + chipH - 1.7);
      doc.setTextColor(0);
      doc.setFont(undefined, "normal");
      doc.setDrawColor(0);
      currentY += chipH + 4;

      // Divisor sutil entre grupos
      doc.setDrawColor(...borderColor);
      doc.line(marginLeft, currentY, pageWidth - marginLeft, currentY);
      doc.setDrawColor(0);
      currentY += 5;
    }

    // =========================
    // L128 (2026-07) — TOTALES (Subtotal / IVA 16% / Total) debajo de los artículos
    // =========================
    {
      // (2026-07) usar SOLO currentY (ya está sincronizado con la última tabla +
      // chip + divisor). Antes se hacía Math.max con doc.autoTable.previous.finalY,
      // pero ese valor es la Y de la página donde TERMINÓ la tabla; si el chip ya
      // saltó de hoja, ese finalY viejo (Y grande de la página anterior) forzaba un
      // salto extra y dejaba páginas casi en blanco (totales/firmas en otra hoja).
      currentY = (currentY || 0) + 2;

      const ivaPDF = subtotalPDF * 0.16;
      const totalConIvaPDF = subtotalPDF + ivaPDF;

      const filasTotales = [
        ["Subtotal:", money(subtotalPDF)],
        ["IVA (16%):", money(ivaPDF)],
        ["Total:", money(totalConIvaPDF)],
      ];

      // caja alineada a la derecha
      const boxW = 70;
      const rowH = 6;
      const boxH = rowH * filasTotales.length + 2;
      const boxX = pageWidth - marginLeft - boxW;

      ensureRoom(boxH + 4);
      const totY = currentY;

      doc.setDrawColor(...borderColor);
      doc.setFillColor(...lightBg);
      doc.roundedRect(boxX, totY, boxW, boxH, 1.5, 1.5, "FD");
      doc.setDrawColor(0);

      const lblXTot = boxX + 3;
      const valXTot = boxX + boxW - 3;
      let lineY = totY + 5;
      filasTotales.forEach(([lbl, val], idx) => {
        const esTotal = idx === filasTotales.length - 1;
        doc.setFont(undefined, "bold");
        doc.setFontSize(esTotal ? 10.5 : 9);
        doc.setTextColor(...(esTotal ? brandColor : labelColor));
        doc.text(lbl, lblXTot, lineY);
        doc.setTextColor(esTotal ? brandColor[0] : 0, esTotal ? brandColor[1] : 0, esTotal ? brandColor[2] : 0);
        doc.text(val, valXTot, lineY, { align: "right" });
        lineY += rowH;
      });
      doc.setFont(undefined, "normal");
      doc.setTextColor(0);
      currentY = totY + boxH + 6;
    }



    dibujarFirmas();


    // =========================
    // BORDADOS (hoja separada, estilo ListadoPedidos)
    // =========================
    try {
      const getCloudinaryUrl = (publicId) => {
        const p = String(publicId || "").trim();
        if (!p) return "";
        if (p.startsWith("http")) return p;
        const base = String(
          process.env.REACT_APP_URL_FOTO_CLOUDINARY || ""
        )
          .replace(/\/$/, "")
          .replace(/\/image\/upload$/, "");
        return base
          ? `${base}/image/upload/${p}`
          : `https://res.cloudinary.com/lynqro/image/upload/${p}`;
      };

      const getImageAsDataURL = (url) =>
        new Promise((resolve) => {
          if (!url) return resolve(null);
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL("image/png"));
            } catch (e) {
              resolve(null);
            }
          };
          img.onerror = () => resolve(null);
          img.src = url;
        });

      const bordadosMap = {};
      (allArticulosOrdenCorte || []).forEach((a) => {
        const area =
          a.areasClientes?.[0]?.name ||
          a.areasClientes?.[0]?._id ||
          "Sin Área";
        // (2026-10) código + línea, igual que la tabla de la orden (antes iba
        // la descripción, que trae textos largos como "MODELO SHYLA SOLICITÓ...")
        const codigo = a.articulos?.[0]?.codigo || "";
        const linea =
          a.articulos?.[0]?.subDepartamentos?.[0]?.name ||
          a.articulos?.[0]?.subDepartamentos?.[0]?.nombre ||
          "";
        const articulo =
          `${codigo}${linea ? " " + linea : ""}`.trim() ||
          a.articulos?.[0]?.descripcion ||
          "Sin Artículo";

        const telaRaw = s(a.tela);
        const telaNombre = telaRaw && telaRaw !== "Sin Tela" ? telaRaw : "";
        const telaComp = s(a.composicion);
        const telaDisplay =
          telaNombre && telaComp
            ? `${telaNombre} - ${telaComp}`
            : telaNombre || telaComp || "Sin Tela";

        const bordados = Array.isArray(a.bordados) ? a.bordados : [];
        if (!bordados.length) return;

        const key = `${area}||${articulo}`;
        if (!bordadosMap[key]) {
          bordadosMap[key] = { area, articulo, tela: telaDisplay, bordados: [] };
        }

        bordados.forEach((b) => {
          const item = {
            observacion: String(
              b?.observacion || b?.observaciones || ""
            ).trim(),
            public_id: String(
              b?.public_id || b?.secure_url || b?.url || ""
            ).trim(),
            version: String(b?.version || ""),
          };
          if (!item.public_id && !item.observacion) return;

          const existe = bordadosMap[key].bordados.some(
            (x) =>
              x.public_id === item.public_id &&
              x.observacion === item.observacion &&
              x.version === item.version
          );
          if (!existe) bordadosMap[key].bordados.push(item);
        });
      });

      const bordadosFinal = Object.values(bordadosMap);

      if (bordadosFinal.length > 0) {
        // capturar el y final para que las firmas queden debajo (no encimadas)
        const yFinBordados = await dibujarFichaBordados(doc, bordadosFinal, {
          getCloudinaryUrl,
          loadImagePDF: getImageAsDataURL,
          pageWidth,
          marginLeft,
          marginTop: 15,
          brandColor,
          lightBg,
          borderColor,
          labelColor,
          s,
        });
        if (yFinBordados) currentY = yFinBordados;
      }
    } catch (errBordados) {
      console.warn(
        "No se pudieron cargar bordados:",
        errBordados
      );
    }

    // ============ FOOTER L36 — pie de página con paginación ============
    try {
      const pageCount = doc.internal.getNumberOfPages();
      for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(...labelColor);
        doc.text(
          `Orden Compra #${s(numero)}  ·  ${s(cliente)}  ·  Página ${p} de ${pageCount}`,
          pageWidth / 2,
          pageHeight - 5,
          { align: "center" }
        );
        doc.setTextColor(0);
      }
    } catch (errFooter) {
      console.warn("No se pudo dibujar footer:", errFooter);
    }

    window.open(
      doc.output("bloburl"),
      "_blank"
    );
  } catch (err) {
    console.error(
      "Error generando PDF:",
      err
    );

    Swal.fire(
      "Error",
      "No se pudo generar el PDF",
      "error"
    );
  }
}

// Argumentos de PDFOC a partir de la orden tal como la regresa el back
// (GET /ordenesCorteExterno/:id), igual que arma el renglón ListadoOrdenesCorteExterno.
function argsDeOrdenCompra(a, { separarPorArea = false, soloHabilitacion = false } = {}) {
  const ped = a.pedidos?.[0] || {};
  const prov = a.proveedores?.[0] || {};
  const nombre = ped.clientes?.[0]?.nombre_comercial || "";
  const sub = String(ped.subCliente ?? "").trim();
  const subArea = String(a.subArea ?? "").trim();
  const obsMonarch = String(ped.observaciones ?? "").trim();
  const obsMarca = String(ped.observacionesMarca ?? "").trim();
  return [
    a.idOrdenesCorteExterno,
    [nombre, sub && sub.toUpperCase() !== "NA" && `(${sub})`, subArea && subArea.toUpperCase() !== "NA" && `(${subArea})`]
      .filter(Boolean)
      .join(" "),
    prov.nombre_comercial || "",
    `${prov.calle || ""} ${prov.numero_ext || ""}, ${prov.numero_int || ""}`,
    prov.email || "",
    a.fechaCompromiso,
    a.totalGeneral,
    a.fecha,
    ped.idPedido || "",
    ped._id || "",
    a.cantidad,
    a.observaciones,
    obsMonarch && obsMonarch.toUpperCase() !== "NA" ? obsMonarch : obsMarca,
    ped.marcas?.[0]?.name || "",
    a._id,
    prov.idProveedor || "",
    ped.nombreFirma || "",
    separarPorArea,
    ped.empresas?.[0]?.razonSocial || ped.empresas?.[0]?.name || "",
    ped.empresas?.[0] || null,
    soloHabilitacion,
  ];
}

// Abre el PDF de una orden de compra con sólo su _id
export async function pdfOrdenCompraPorId(idOrden, opciones) {
  try {
    const { data } = await axios.get(`${URL_ORDENES_CORTE_EXTERNO}/${idOrden}`, {
      headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
    });
    if (!data?._id) throw new Error("Orden no encontrada");
    await PDFOC(...argsDeOrdenCompra(data, opciones));
  } catch (err) {
    console.error("Error abriendo la orden de compra:", err);
    Swal.fire("Error", "No se pudo abrir la orden de compra", "error");
  }
}
